import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { filter, switchMap, take } from 'rxjs';
import { AppStateService } from './app-state.service';

/**
 * Holds off http requests while the app is in the background, and releases them on resume.
 * Applies to every request — work in a suspended webview is wasted either way, and the OS
 * may not let it finish.
 *
 * Independent of whether the user is logged in, which is why it sits outside `auth/`.
 * Waiting for the token check concerns only requests that carry one and therefore happens
 * in the auth interceptor, behind its `AUTHENTICATE` flag — a public endpoint does not pay
 * for the login state of the app. `main.ts` registers this interceptor first, so the
 * background pause wraps that wait rather than the other way round.
 *
 * Only the state at the start of a request is considered; a request already in flight is
 * not cancelled when the app is backgrounded.
 */
export const gateInterceptor: HttpInterceptorFn = (req, next) => {
  const appState = inject(AppStateService);
  return appState.isActive$.pipe(
    filter(Boolean),
    take(1),
    switchMap(() => next(req)),
  );
};
